import React from 'react';
import {
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
  Box,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import PersonIcon from '@mui/icons-material/Person';

const StyledFormControl = styled(FormControl)({
  minWidth: 120,
  width: 'auto',
  maxWidth: '100%'
});

const SelectedGender = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
});

const GenderIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * Voice gender selector component
 * @param {Object} props Component props
 * @param {string} props.value Current gender value
 * @param {Function} props.onChange Change handler
 * @param {Array} props.options Array of gender options
 * @returns {JSX.Element} GenderSelector component
 */
const GenderSelector = ({ 
  value, 
  onChange, 
  options = [
    { value: 'MALE', label: 'Male', icon: <MaleIcon /> },
    { value: 'FEMALE', label: 'Female', icon: <FemaleIcon /> }
  ],
  ...props
}) => {
  // Automatically disable the selector if there's only one option available
  const isSingleOption = options.length === 1;
  // Create a map of gender values to objects for easy lookup
  const optionsMap = React.useMemo(() => {
    return options.reduce((acc, option) => {
      acc[option.value] = option;
      return acc;
    }, {});
  }, [options]);

  return (
    <StyledFormControl {...props}>
      <InputLabel id="gender-select-label">Voice Gender</InputLabel>
      <Select
        labelId="gender-select-label"
        id="gender-select"
        value={value}
        label="Voice Gender"
        onChange={onChange}
        disabled={isSingleOption}
        renderValue={(selected) => {
          const option = optionsMap[selected] || { value: selected, label: selected, icon: <PersonIcon /> };
          return (
            <SelectedGender>
              <GenderIcon>{option.icon}</GenderIcon>
              <Typography variant="body1">{option.label}</Typography>
            </SelectedGender>
          );
        }}
      >
        {options.map((option, index) => (
          <MenuItem key={`${option.value}-${index}`} value={option.value}>
            <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
              {option.icon}
            </ListItemIcon>
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </StyledFormControl>
  );
};

export default GenderSelector;
